import { type ClassValue, clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'
import type { DocumentMetadata, Task, TaskStats } from '../../shared/types'
import { format, getCurrentDate, getCurrentDateTime, parse } from '../../shared/date-fns'

// Utility function for shadcn/ui components
export function cn(...inputs: ClassValue[]) {
	return twMerge(clsx(inputs))
}

// Format date strings with Asia/Bangkok timezone
export function formatDate(dateString: string): string {
	return format(dateString, 'MMM dd, yyyy HH:mm')
}

// Parse date with Asia/Bangkok timezone
export function parseDate(dateString: string): Date {
	return parse(dateString)
}

// Re-export from shared utilities
export { getCurrentDate, getCurrentDateTime }

// Calculate task statistics
export function calculateTaskStats(tasks: Task[]): TaskStats {
	return tasks.reduce(
		(stats, task) => {
			stats.total++
			stats[task.status]++
			return stats
		},
		{ total: 0, pending: 0, in_review: 0, completed: 0 }
	)
}

// Calculate progress percentage
export function calculateProgress(approved: number, total: number): number {
	if (total === 0) return 0
	return Math.round((approved / total) * 100)
}

// Parse document metadata
export function parseMetadata(metadata: string | null): DocumentMetadata | null {
	if (!metadata) return null
	try {
		return JSON.parse(metadata)
	} catch {
		return null
	}
}

// Parse document content
export function parseDocumentContent(content: string): Record<string, unknown> | null {
	try {
		return JSON.parse(content)
	} catch {
		return null
	}
}

// Format confidence score as percentage
export function formatConfidence(confidence: number | null): string {
	if (confidence === null) return 'N/A'
	return `${Math.round(confidence * 100)}%`
}

// Get confidence level for styling
export function getConfidenceLevel(confidence: number | null): 'high' | 'medium' | 'low' {
	if (confidence === null) return 'low'
	if (confidence >= 0.8) return 'high'
	if (confidence >= 0.6) return 'medium'
	return 'low'
}

// Capitalize first letter
export function capitalize(str: string): string {
	return str.charAt(0).toUpperCase() + str.slice(1)
}
